/**
 * Enhanced auth interceptor configuration
 * Handles different types of API calls with appropriate auth checks
 */

export interface AuthInterceptorConfig {
  // Paths that should skip auth check completely
  skipAuthCheckPaths: string[]
  
  // Paths that should trigger immediate auth check
  immediateAuthCheckPaths: string[]
  
  // Default interval for auth checks (in milliseconds)
  defaultAuthCheckInterval: number
  
  // Interval for immediate auth checks (in milliseconds)
  immediateAuthCheckInterval: number
}

export const defaultAuthInterceptorConfig: AuthInterceptorConfig = {
  skipAuthCheckPaths: [
    '/auth/me',
    '/auth/login', 
    '/auth/register', 
    '/auth/logout', 
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/verify-email',
    '/auth/resend-link'
  ],
  
  immediateAuthCheckPaths: [
    '/stripe/',
    '/subscription',
    '/patients',
    '/devices',
    '/dashboard'
  ],
  
  defaultAuthCheckInterval: 120000, // 2 minutes for regular API calls (increased from 30s)
  immediateAuthCheckInterval: 30000  // 30 seconds for critical paths (increased from 5s)
}

/**
 * Determine if a URL should skip auth check
 */
export const shouldSkipAuthCheck = (url: string, config: AuthInterceptorConfig = defaultAuthInterceptorConfig): boolean => {
  return config.skipAuthCheckPaths.some(path => url?.includes(path))
}

/**
 * Determine if request headers indicate auth check should be skipped
 */
export const shouldSkipAuthCheckByHeaders = (headers: any): boolean => {
  return headers?.skipAuth === 'true' || headers?.skipAuth === true
}

/**
 * Determine if a URL should trigger immediate auth check
 */
export const shouldTriggerImmediateAuthCheck = (url: string, config: AuthInterceptorConfig = defaultAuthInterceptorConfig): boolean => {
  return config.immediateAuthCheckPaths.some(path => url?.includes(path))
}

/**
 * Get the appropriate auth check interval for a URL
 */
export const getAuthCheckInterval = (url: string, config: AuthInterceptorConfig = defaultAuthInterceptorConfig): number => {
  if (shouldTriggerImmediateAuthCheck(url, config)) {
    return config.immediateAuthCheckInterval
  }
  return config.defaultAuthCheckInterval
}
